import services from "../services";
import Utils from "../Utils";
import NestMoney from "../NestMoney";

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const strict = true;

const getDefaultState = () => {
  return {
    searchQuery: null,
    pagination: {
      itemsPerPage: 10,
      activePage: 1
    },
    servicesList: [],
    serviceAvailabilityDuration: {
      value: 0,
      timeType: "MINUTE"
    },
    activeService: null,
    doctorsListOfService: [],
    currentPagination: {
      sortBy: ["label"],
      mustSort: true,
      sortDesc: [false],
      page: 1,
      itemsPerPage: 10,
      multiSort: false
    }
  };
};

const state = getDefaultState();

const getters = {
  currentPagination: state => state.currentPagination,
  paginationItemsPerPage: state => state.pagination.itemsPerPage,
  paginationActivePage: state => state.pagination.activePage,
  searchQuery: state => state.searchQuery,
  doctorsListOfService: state => state.doctorsListOfService,
  serviceAvailabilityDuration: state => state.serviceAvailabilityDuration,
  servicesList: state => state.servicesList,
  activeServiceList: state => state.servicesList.filter(s => s.type != null),
  activeService: state => state.activeService,
  activeServiceTimetable: state => state.activeService.serviceTimeTable,
  activeServiceSlots: state => state.activeService.periodSlotList
};
const mutations = {
  RESET(state) {
    // acquire initial state
    const s = getDefaultState();
    Object.keys(s).forEach(key => {
      state[key] = s[key];
    });
  },
  SET_CURRENT_PAGINATION(state, currPagination) {
    state.currentPagination = currPagination;
  },
  SET_PAGINATION_ITEMS_PER_PAGE(state, itemsPerPage) {
    state.pagination.itemsPerPage = itemsPerPage;
  },
  SET_PAGINATION_ACTIVE_PAGE(state, activePage) {
    state.pagination.activePage = activePage;
  },
  SET_SEARCH_QUERY(state, query) {
    state.searchQuery = query;
  },
  SET_ACTIVE_SERVICE(state, serviceId) {
    state.activeService = state.servicesList.find(
      s => s.serviceId == serviceId
    );
  },
  SET_SERVICE_TIMETABLE(state, serviceTimeTable) {
    state.activeService.serviceTimeTable = serviceTimeTable;
  },
  SET_SERVICE_SLOTS(state, slots) {
    state.activeService.periodSlotList = slots;
  },
  SET_SERVICE_AVAILABILITY_DURATION(state, duration) {
    state.serviceAvailabilityDuration = {
      ...duration
    };
  },
  SET_DOCTORS_LIST_OF_SERVICE(state, doctorsListOfService) {
    state.doctorsListOfService = Object.freeze(doctorsListOfService);
  },
  SET_SERVICES_LIST(state, servicesList) {
    state.servicesList = servicesList;
  },
  SET_SERVICE(state, newService) {
    let oldService = state.servicesList.find(
      s => s.serviceId == newService.serviceId
    );
    Object.assign(oldService, newService);
  },
  SET_SERVICE_ALIAS(state, { serviceId, alias }) {
    let services = state.servicesList.map(s => {
      if (s.serviceId === serviceId) {
        s.alias = alias;
        s.serviceAlias = alias?.it_IT || "";
      }
      return s;
    });
    state.servicesList = services;
  }
};

const actions = {
  // GET LIST OF DOCTORS FOR A SPECIFIC SERVICE
  fetchDoctorsOfService({ commit }, { officeId, serviceId }) {
    return services.hfManagementService
      .fetchDoctorsListOfService(officeId, serviceId)
      .then(doctors => {
        commit("SET_DOCTORS_LIST_OF_SERVICE", doctors);
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "hfManagement";
        }

        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
      })
      .finally(() => {});
  },
  // GET LIST OF SERVICES FOR A SPECIFIC OFFICE
  /**
   * @param  {} {commit
   * @param  {} state
   * @param  {} dispatch}
   * @param  {} officeId
   */
  fetchOfficeServices({ commit }, officeId) {
    return services.hfManagementService
      .fetchHFServices(officeId)
      .then(servicesList => {
        servicesList.forEach(function (service) {
          service["serviceName"] = service?.label?.defaultValue || "";
          service["serviceShortName"] = service?.label?.shortValue || "";
          service["serviceAlias"] = service?.alias?.it_IT || "";
          service["servicePrice"] =
            NestMoney.formatCurrencyForFE(service?.price) ||
            NestMoney.formatCurrencyForFE("EUR 0.00");
        });
        servicesList.sort((s1, s2) =>
          s1.serviceName.localeCompare(s2.serviceName)
        );
        commit("SET_SERVICES_LIST", servicesList);
        return servicesList;
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "hfManagement";
        }

        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
      })
      .finally(() => {});
  },

  // UPDATE A SERVICE
  /**
   * @param  {} {commit
   * @param  {} state}
   * @param  {} payload
   */
  updateService({ commit }, { officeId, service }) {
    return services.hfManagementService
      .updateService(officeId, service)
      .then(result => {
        commit("SET_SERVICE", service);
        return Promise.resolve(result);
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "hfManagement";
        }

        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  /**
   * Save or update a service alias
   */
  callSaveOrUpdateServiceAlias({ commit }, { officeId, serviceId, alias }) {
    return services.hfManagementService
      .saveOrUpdateServiceAlias(officeId, serviceId, alias)
      .then(() => {
        commit("SET_SERVICE_ALIAS", {
          serviceId,
          alias
        });
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "hfManagement";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
      })
      .finally(() => {});
  },
  /**
   * Delete a service alias
   */
  callDeleteServiceAlias({ commit }, payload) {
    return services.hfManagementService
      .deleteServiceAlias(payload)
      .then(() => {
        let serviceId = payload.serviceId;
        let alias = null;
        commit("SET_SERVICE_ALIAS", {
          serviceId,
          alias
        });
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "hfManagement";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
      })
      .finally(() => {});
  },
  // GET SERVICE TIMETABLE
  /**
   * @param  {} {commit
   * @param  {} state}
   * @param  {} serviceId
   */
  fetchServiceTimetable({ commit }, { officeId, serviceId }) {
    return services.hfManagementService
      .fetchServiceTimetable(officeId, serviceId)
      .then(serviceTimeTable => {
        commit("SET_SERVICE_TIMETABLE", serviceTimeTable);
        return Promise.resolve();
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "hfManagement";
        }

        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  async saveServiceTimetable({ commit }, payload) {
    let serviceTimetable = payload.availability.daysTimespans;
    let timetable = {
      serviceTimeTable: serviceTimetable
    };
    return await services.hfManagementService
      .saveServiceTimetable(payload.officeId, payload.serviceId, timetable)
      .then(() => {
        return Promise.resolve();
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "serviceManagement.availabilityForm";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject(error);
      })
      .finally(() => {});
  },
  // GET SERVICE TIMETABLE
  /**
   * @param  {} {commit
   * @param  {} state}
   * @param  {} serviceId
   */
  fetchServiceSlots({ commit }, { officeId, serviceId }) {
    return services.hfManagementService
      .fetchServiceSlots(officeId, serviceId)
      .then(availability => {
        commit("SET_SERVICE_SLOTS", availability.periodSlotList);
        commit("SET_SERVICE_AVAILABILITY_DURATION", availability.duration);
        return Promise.resolve(availability);
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "hfManagement";
        }

        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject(`Cannot get service'
          s slots`);
      })
      .finally(() => {});
  },
  /**
   * Save list of slots for a specific service (type == SLOT)
   * @param  {} {commit
   * @param  {} state}
   * @param  {} payload
   */
  async saveServiceSlots({ commit }, payload) {
    let slots = {
      periodSlot: payload.availability.daysTimespans,
      duration: {
        value: payload.availability.duration.value,
        timeType: payload.availability.duration.timeType
      }
    };
    return await services.hfManagementService
      .saveServiceSlots(payload.officeId, payload.serviceId, slots)
      .then(() => {
        return Promise.resolve();
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "serviceManagement.availabilityForm";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject(`Error trying to save service's slots`);
      })
      .finally(() => {});
  },
  async exportOfficeServicesExcelFile(
    { commit },
    { officeId, officeServiceIds, officeName } = {}
  ) {
    try {
      const ids = officeServiceIds?.length
        ? officeServiceIds.join(",")
        : undefined;
      const { data } =
        await services.hfManagementService.generateOfficeServicesExcelFile(
          officeId,
          ids
        );
      Utils.downloadBlob(
        data,
        `Servizi della struttura ${Utils.sanitizeFileName(officeName)}.xlsx`,
        data?.type || XLSX_MIME
      );
    } catch (reason) {
      commit("error/SET_ERROR", Utils.toErrorPayload(reason), { root: true });
      throw reason;
    }
  }
};

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions
};
